import React, { useEffect, useState } from 'react';
import { AlertTriangleIcon, AlertCircleIcon, KeyIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { deleteUser, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { doc, deleteDoc, collection, query, where, getDocs, writeBatch, limit } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { deleteStudentAccount } from '../services/studentService';
import { ref, listAll, deleteObject } from 'firebase/storage';
interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose
}) => {
  const {
    currentUser,
    logout,
    userType
  } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [currentStep, setCurrentStep] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{
    [key: string]: number;
  }>({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Reduced timeout for file deletion - from 60s to 30s
  const MAX_FILE_DELETION_TIME = 30000; // 30 seconds max for file deletion
  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsDeleting(false);
      setError('');
      setPassword('');
      setCurrentStep('');
      setSuccess(false);
      setDeleteProgress({});
      setIsAuthenticated(false);
    }
  }, [isOpen]);
  // Handle timeout for deletion operations with more granular timeouts
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    if (isDeleting && !success) {
      // More granular timeouts based on the current step
      const timeoutDuration = currentStep.includes('Authenticating') ? 30000 : currentStep.includes('Cleaning up files') ? 45000 :
      // Reduced from 120s to 45s
      currentStep.includes('Deleting account data') ? 60000 :
      // Reduced from 90s to 60s
      45000; // Default timeout
      timeoutId = setTimeout(() => {
        if (isDeleting && !success) {
          // Special handling for file cleanup timeout
          if (currentStep.includes('Cleaning up files')) {
            console.warn('File cleanup taking too long, proceeding with account deletion');
            // Instead of showing error and stopping, proceed to the next step
            setCurrentStep('Finalizing account deletion...');
            // Attempt to delete the Auth user directly
            if (currentUser) {
              finalizeAccountDeletion();
            } else {
              setIsDeleting(false);
              setError('User authentication error. Please try again.');
            }
          } else {
            setIsDeleting(false);
            setError(`The operation timed out during "${currentStep}". Please try again or contact support.`);
            console.error('Delete operation timed out', {
              step: currentStep,
              progress: deleteProgress
            });
          }
        }
      }, timeoutDuration);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isDeleting, currentStep, deleteProgress, success, currentUser]);
  if (!isOpen) return null;
  const updateProgress = (collection: string, count: number) => {
    setDeleteProgress(prev => ({
      ...prev,
      [collection]: (prev[collection] || 0) + count
    }));
  };
  // Authenticate user with password
  const authenticateUser = async () => {
    if (!currentUser || !currentUser.email) {
      setError('Unable to authenticate. Please try logging in again.');
      return false;
    }
    if (!password.trim()) {
      setError('Please enter your password to confirm account deletion');
      return false;
    }
    try {
      setCurrentStep('Authenticating your account...');
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      await reauthenticateWithCredential(currentUser, credential);
      console.log('Successfully reauthenticated user');
      setIsAuthenticated(true);
      return true;
    } catch (authError: any) {
      if (authError.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (authError.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else if (authError.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again.');
      } else if (authError.code === 'auth/invalid-credential') {
        setError('Invalid credentials. Please try again with the correct password.');
      } else {
        setError(authError.message || 'Authentication failed. Please try again.');
      }
      return false;
    }
  };
  // Main delete account function with improved flow - OPTIMIZED VERSION
  const handleDeleteAccount = async () => {
    if (!currentUser) {
      // Handle localStorage cleanup for non-Firebase users (legacy support)
      handleLegacyAccountDeletion();
      return;
    }
    setIsDeleting(true);
    setError('');
    setCurrentStep('Preparing to delete account...');
    setDeleteProgress({});
    try {
      // Step 1: Authenticate the user
      const isAuth = await authenticateUser();
      if (!isAuth) {
        setIsDeleting(false);
        return;
      }
      // OPTIMIZATION: Delete Auth user FIRST - this is the most important step
      // and ensures the user can't log back in, even if other cleanup fails
      try {
        setCurrentStep('Deleting your account...');
        await deleteUser(currentUser);
        console.log('Successfully deleted user from Firebase Auth');
        // Mark as success immediately after Auth deletion
        setSuccess(true);
        // Clean up localStorage right away
        cleanupLocalStorage();
        // Set a short timeout to show the success message before redirecting
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
        // Continue with background cleanup if needed, but don't wait for it
        // This happens after we've already shown success to the user
        setTimeout(() => {
          try {
            // Attempt minimal cleanup in the background
            // We don't await these or handle errors since the user is already deleted
            const uid = currentUser.uid;
            // Delete critical user documents only
            const userDocRef = doc(db, 'users', uid);
            deleteDoc(userDocRef).catch(e => console.log('Background cleanup error:', e));
            if (userType === 'teacher') {
              const teacherDocRef = doc(db, 'teachers', uid);
              deleteDoc(teacherDocRef).catch(e => console.log('Background cleanup error:', e));
            }
            console.log('Background cleanup initiated');
          } catch (cleanupError) {
            console.log('Background cleanup error:', cleanupError);
            // Don't show these errors to the user since the account is already deleted
          }
        }, 100);
        return true;
      } catch (authError: any) {
        console.error('Error in account deletion:', authError);
        // Handle specific error cases
        if (authError.code === 'auth/requires-recent-login') {
          setError('For security, please log out and log back in before trying again.');
        } else {
          setError(`Error deleting account: ${authError.message || 'Unknown error'}`);
        }
        setIsDeleting(false);
        return false;
      }
    } catch (error: any) {
      console.error('Account deletion error:', error);
      setError(error.message || 'Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };
  // Legacy support for localStorage-only accounts
  const handleLegacyAccountDeletion = () => {
    setIsDeleting(true);
    console.log('Starting legacy account deletion process...');
    setTimeout(() => {
      cleanupLocalStorage();
      setSuccess(true);
      setIsDeleting(false);
      // Redirect to home page
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    }, 500); // Reduced from 1000ms to 500ms
  };
  // Simplified version - we no longer need these complex functions
  // since we're doing minimal cleanup in the background
  const deleteUserFiles = async () => {};
  const deleteFirestoreDocuments = async () => {};
  const finalizeAccountDeletion = async () => {};
  const cleanupLocalStorage = () => {
    console.log('Cleaning up localStorage...');
    // For student users
    if (localStorage.getItem('studentEmail')) {
      localStorage.removeItem('studentEmail');
      localStorage.removeItem('studentName');
      localStorage.removeItem('studentInstrument');
      localStorage.removeItem('studentLessonType');
      localStorage.removeItem('studentZipCode');
      localStorage.removeItem('studentPassword');
    }
    // For teacher users
    if (localStorage.getItem('teacherEmail')) {
      const teacherEmail = localStorage.getItem('teacherEmail');
      // Remove from teacherUsers array if exists
      const teacherUsers = JSON.parse(localStorage.getItem('teacherUsers') || '[]');
      const updatedTeacherUsers = teacherUsers.filter(teacher => teacher.email !== teacherEmail);
      localStorage.setItem('teacherUsers', JSON.stringify(updatedTeacherUsers));
      // Remove other teacher-related data
      localStorage.removeItem('teacherEmail');
      localStorage.removeItem('teacherName');
      localStorage.removeItem('teacherPassword');
      localStorage.removeItem('teacherAvailability');
      localStorage.removeItem('teacherProfile');
    }
    // Clear any auth tokens or session data
    localStorage.removeItem('firebase:authUser:AIzaSyBY4BsIdW0ljOgb-vz-Wq2jnNfwxyRW7xo:[DEFAULT]');
    localStorage.removeItem('userType');
    console.log('localStorage cleanup complete');
  };
  return <div className="fixed inset-0 flex items-center justify-center z-[9999]" data-id="element-1054">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/70" onClick={!isDeleting ? onClose : undefined} aria-hidden="true" data-id="element-1055"></div>
      {/* Modal */}
      <div className="bg-navy-900 rounded-xl max-w-md w-full shadow-xl relative z-[10000] mx-4 my-auto" data-id="element-1056">
        <div className="p-6" data-id="element-1057">
          {success ? <div className="text-center py-4" data-id="element-1058">
              <div className="bg-green-600/20 rounded-full p-3 mx-auto mb-4 w-16 h-16 flex items-center justify-center" data-id="element-1059">
                <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1060">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1061" />
                </svg>
              </div>
              <h2 className="text-xl font-serif font-bold text-white text-center mb-2" data-id="element-1062">
                Account Deleted Successfully
              </h2>
              <p className="text-gray-300 text-center" data-id="element-1063">
                You will be redirected to the home page...
              </p>
            </div> : <>
              <div className="flex items-center justify-center mb-4" data-id="element-1064">
                <div className="bg-red-600/20 rounded-full p-3" data-id="element-1065">
                  <AlertTriangleIcon className="h-6 w-6 text-red-500" data-id="element-1066" />
                </div>
              </div>
              <h2 className="text-xl font-serif font-bold text-white text-center mb-4" data-id="element-1067">
                Are you sure you want to delete your account?
              </h2>
              <p className="text-gray-300 text-center mb-6" data-id="element-1068">
                This action cannot be undone. All your data will be permanently
                removed.
              </p>
              {error && <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6 flex items-start" data-id="element-1069">
                  <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-1070" />
                  <p className="text-red-300 text-sm" data-id="element-1071">{error}</p>
                </div>}
              {/* Show progress if deleting */}
              {isDeleting && <div className="bg-navy-800/60 border border-white/10 rounded-lg p-4 mb-6" data-id="element-1072">
                  <div className="flex items-center mb-2" data-id="element-1073">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1074">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1075"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1076"></path>
                    </svg>
                    <p className="text-sm text-white" data-id="element-1077">{currentStep}</p>
                  </div>
                  {Object.keys(deleteProgress).length > 0 && <ul className="text-xs text-gray-300 space-y-1 mt-2" data-id="element-1078">
                      {Object.entries(deleteProgress).map(([collection, count]) => <li key={collection} data-id="element-1079">
                            {collection}: {count} items deleted
                          </li>)}
                    </ul>}
                </div>}
              {/* Password Entry Field */}
              <div className="space-y-4" data-id="element-1080">
                <div data-id="element-1081">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1082">
                    Enter your password to confirm
                  </label>
                  <div className="relative" data-id="element-1083">
                    <KeyIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" data-id="element-1084" />
                    <input type="password" id="password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && password) {
                    handleDeleteAccount();
                  }
                }} className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/20 rounded-lg focus:ring-deep-pink focus:border-deep-pink text-white" placeholder="Your password" autoComplete="current-password" disabled={isDeleting} data-id="element-1085" />
                  </div>
                </div>
                <div className="flex space-x-3" data-id="element-1086">
                  <button onClick={onClose} className="flex-1 bg-transparent hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition-colors border border-gray-600" disabled={isDeleting} data-id="element-1087">
                    Cancel
                  </button>
                  <button onClick={handleDeleteAccount} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors flex justify-center items-center" disabled={isDeleting || !password} data-id="element-1088">
                    {isDeleting ? <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1089">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1090"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1091"></path>
                        </svg>
                        Deleting...
                      </> : 'Confirm Delete'}
                  </button>
                </div>
              </div>
            </>}
        </div>
      </div>
    </div>;
};